import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const EMOJIS = ["🎮", "👾", "🕹️", "🏆", "⚡", "🚀", "💎", "🔥"];

function buildDeck() {
  const cards = [...EMOJIS, ...EMOJIS]
    .map((e, i) => ({ id: i, emoji: e, flipped: false, matched: false }))
    .sort(() => Math.random() - 0.5);
  return cards;
}

export function MemoryMatch({ onScore }) {
  const [cards, setCards] = useState(buildDeck);
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [time, setTime] = useState(0);
  const [done, setDone] = useState(false);
  const reported = useRef(false);
  const lock = useRef(false);

  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [done]);

  const reset = () => {
    setCards(buildDeck()); setFlipped([]); setMoves(0); setMatches(0);
    setTime(0); setDone(false); reported.current = false; lock.current = false;
  };

  const flip = (idx) => {
    if (lock.current || cards[idx].flipped || cards[idx].matched || flipped.length === 2) return;
    const nc = cards.map((c, i) => (i === idx ? { ...c, flipped: true } : c));
    const nf = [...flipped, idx];
    setCards(nc);
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves((m) => m + 1);
      lock.current = true;
      const [a, b] = nf;
      if (nc[a].emoji === nc[b].emoji) {
        setTimeout(() => {
          setCards((cur) => cur.map((c, i) => (i === a || i === b ? { ...c, matched: true } : c)));
          setFlipped([]);
          lock.current = false;
          setMatches((mm) => {
            const total = mm + 1;
            if (total === EMOJIS.length) setDone(true);
            return total;
          });
        }, 450);
      } else {
        setTimeout(() => {
          setCards((cur) => cur.map((c, i) => (i === a || i === b ? { ...c, flipped: false } : c)));
          setFlipped([]);
          lock.current = false;
        }, 800);
      }
    }
  };

  useEffect(() => {
    if (done && !reported.current) {
      reported.current = true;
      const score = Math.max(50, 1000 - moves * 20 - time * 2);
      onScore?.(score);
    }
  }, [done, moves, time, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="memory-game">
      <div className="flex items-center gap-6 mb-4 font-mono text-sm">
        <span className="text-neon-cyan">MOVES: {moves}</span>
        <span className="text-neon-lime">PAIRS: {matches}/{EMOJIS.length}</span>
        <span className="text-neon-yellow">TIME: {time}s</span>
        <Button onClick={reset} variant="outline" size="sm" className="border-zinc-700 rounded-none text-white h-8">
          <RotateCcw className="w-3 h-3 mr-1" /> Reset
        </Button>
      </div>

      <div className="relative grid grid-cols-4 gap-3" style={{ width: "min(92vw, 400px)" }}>
        {cards.map((c, i) => (
          <button key={c.id} onClick={() => flip(i)} data-testid={`memory-card-${i}`}
            className={`aspect-square rounded-lg text-3xl flex items-center justify-center border-2 transition-all duration-300 ${
              c.flipped || c.matched
                ? "bg-zinc-800 border-neon-cyan"
                : "bg-zinc-950 border-zinc-700 hover:border-zinc-500"
            } ${c.matched ? "opacity-60" : ""}`}>
            {(c.flipped || c.matched) ? c.emoji : ""}
          </button>
        ))}

        {done && (
          <div className="absolute inset-0 bg-black/90 rounded-lg flex flex-col items-center justify-center gap-3" data-testid="memory-complete">
            <p className="font-display font-black text-2xl text-neon-lime uppercase">You Win!</p>
            <p className="font-mono text-white text-sm">{moves} moves · {time}s</p>
            <Button onClick={reset} className="bg-neon-cyan text-black rounded-none font-bold uppercase">Play Again</Button>
          </div>
        )}
      </div>
    </div>
  );
}
